import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { TableAction, TableActionDisplay, TableActionOptions, TableFilterProperties, TablePageProperties } from "../model/action.model";

import { DateTime } from "luxon";
import { IFilters } from "./filter.service";

export type TableActionFunction = () => void;

const isSearchable = data => typeof data === "string" || typeof data === "number";

const matchesSearchTerm = (dataItem, columnsToSearch: string[], searchTerm: string) =>
    columnsToSearch.some(
        column =>
            (column &&
                dataItem.hasOwnProperty(column) &&
                isSearchable(dataItem[column]) &&
                dataItem[column]?.toString()?.toLowerCase()?.includes(searchTerm)) ||
            (Array.isArray(dataItem[column]) && dataItem[column].some(item => item.toString()?.toLowerCase()?.includes(searchTerm)))
    );

const BOOLEAN_COLS: { [key: string]: [string /* positive expected value */, string] /* negative expected value */ } = {
    inactive: ["suspended", "active"],
    isOccupied: ["isOccupied", "unOccupied"],
};

const matchesFilter = (data, filters: IFilters) => {
    for (const column in filters) {
        if (filters[column]) {
            const dataItemColumnHasValue = filters[column].some(filterName => {
                // if the project has the filterKey
                if (data.hasOwnProperty(column)) {
                    // and it is not the customFilters key
                    let value = filterName;
                    if (typeof value === "object" && value) {
                        value = value[0];
                    }

                    let dataValue = data[column];
                    if (column.includes("Status") && typeof dataValue === "object" && dataValue !== null) {
                        dataValue = dataValue[0];
                    }

                    // boolean value cols
                    const boolCol: string = Object.keys(BOOLEAN_COLS).find(key => column === key);
                    if (boolCol) {
                        const boolColDef: [string, string] = BOOLEAN_COLS[boolCol];
                        if (filterName === boolColDef[0]) {
                            return !!dataValue;
                        }

                        return !dataValue;
                    }

                    if (value === "lockoutTime" && dataValue?.length) {
                        return true;
                    }

                    // check to see if the filter name
                    // equals the project property value
                    return (value || "").toLowerCase() === (dataValue || "").toLowerCase();
                }
            });

            if (!dataItemColumnHasValue) {
                return false;
            }
        }
    }

    return true;
};

export class TablePropertiesService {
    static tableFilterProperties: TableFilterProperties = {
        selectedFilters: {},
        searchTerm: "",
        columnsToSearch: [],
    };

    static tablePageProperties: TablePageProperties = {
        pageSize: 20,
        pageIndex: 0,
        active: "",
        direction: "asc",
        dataSortType: "default",
    };

    static getTableFilterProperties$(updatedProps?: Partial<TableFilterProperties>) {
        return new BehaviorSubject<TableFilterProperties>({ ...this.tableFilterProperties, ...updatedProps });
    }

    static getTablePageProperties$(updatedProps?: Partial<TablePageProperties>) {
        return new BehaviorSubject<TablePageProperties>({ ...this.tablePageProperties, ...updatedProps });
    }

    static implementAction(title: string, emitAction: TableActionFunction, icon = ""): TableAction {
        return { title, emitAction, icon };
    }

    static implementDelete(emitAction: TableActionFunction, icon = "fp-icon-trash-bin"): TableAction {
        return this.implementAction($localize`:@@SHARED.TEXT.DELETE:Delete`, emitAction, icon);
    }

    static implementSuspend(emitAction: TableActionFunction, icon = "fp-icon-block"): TableAction {
        return this.implementAction($localize`:@@SHARED.TEXT.SUSPEND:Suspend`, emitAction, icon);
    }

    static implementActivate(emitAction: TableActionFunction, icon = "fp-icon-ok-circled2"): TableAction {
        return this.implementAction($localize`:@@SHARED.TEXT.ACTIVATE:Activate`, emitAction, icon);
    }

    static getActionOptions<T>(getActions: (row: T) => TableAction[], updatedOptions?: Partial<TableActionOptions>) {
        return {
            selectionListener: (selectedRow: T, subject: Subject<TableAction[]>) => {
                if (selectedRow) {
                    subject.next(getActions(selectedRow));
                } else {
                    subject.next([]);
                }
            },
            display: TableActionDisplay.separated,
            ...updatedOptions,
        };
    }

    static updateTableProperties(
        tableProperties: BehaviorSubject<TableFilterProperties>,
        updatedProperties: Partial<TableFilterProperties>
    ) {
        const selectedFilters = updatedProperties?.selectedFilters;

        if (selectedFilters) {
            updatedProperties.selectedFilters = { ...tableProperties.value.selectedFilters, ...updatedProperties.selectedFilters };

            for (const filterKey in selectedFilters) {
                if (selectedFilters[filterKey] && !selectedFilters[filterKey].length) {
                    delete updatedProperties.selectedFilters[filterKey];
                }
            }
        }

        tableProperties.next({ ...tableProperties.value, ...updatedProperties });

        return tableProperties;
    }

    static getDataSource$(
        isMobileView$: Observable<boolean>,
        tableProperties$: BehaviorSubject<TableFilterProperties>,
        getSelector: (isMobile: boolean) => Observable<any[]>
    ) {
        return isMobileView$.pipe(
            switchMap(isMobileView => getSelector(isMobileView)),
            switchMap(data => tableProperties$.pipe(map(props => this.searchData(data, props))))
        );
    }
    static naturalSort(s1, s2, direction) {
        if (direction === "desc") {
            if (s1 < s2) {
                return 1;
            }
            if (s1 > s2) {
                return -1;
            }
            return 0;
        }
        if (s1 < s2) {
            return -1;
        }
        if (s1 > s2) {
            return 1;
        }
        return 0;
    }

    static sortData<T = any>(data: T[], { active, direction, dataSortType }: Partial<TablePageProperties>): T[] {
        data.sort((a, b) => {
            if (dataSortType === "datetime") {
                return TablePropertiesService.dateSort(direction, a[active], b[active]);
            }
            if (isNaN(+a[active]) || isNaN(+b[active])) {
                return direction === "desc"
                    ? (b[active]?.toString().trim() || "")?.localeCompare(a[active]?.toString().trim() || "")
                    : (a[active]?.toString().trim() || "")?.localeCompare(b[active]?.toString().trim() || "");
            }
            // // sorting as number
            return direction === "desc" ? +b[active] - +a[active] : +a[active] - +b[active];
        });

        return data;
    }

    static dateSort(direction, d1: string, d2: string, format?: string) {
        if (!d1) {
            return direction === "desc" ? 1 : -1;
        }
        if (!d2) {
            return direction === "desc" ? -1 : 1;
        }

        // MMMMM d, yyyy is equal to DateTime.DATE_FULL as formatter, but I haven't figured out the built-in constant
        const v1 = DateTime.fromFormat(d1, format || "MMMM d, yyyy");
        const v2 = DateTime.fromFormat(d2, format || "MMMM d, yyyy");

        return direction === "desc" ? +v2 - +v1 : +v1 - +v2;
    }

    static paginateData<T = any>(data: T[], { pageSize }: Partial<TablePageProperties>): T[][] {
        const displayData = [];

        for (let i = 0; i < data.length; i += pageSize) {
            displayData.push(data.slice(i, i + pageSize));
        }

        return displayData;
    }

    static searchData<T = any>(data: T[], { selectedFilters, searchTerm, columnsToSearch }: TableFilterProperties) {
        const filterKeys = Object.keys(selectedFilters || {});
        let filtered = data;
        if (searchTerm || filterKeys) {
            filtered = data.filter(dataItem => {
                const containsFilter = !filterKeys?.length || matchesFilter(dataItem, selectedFilters);
                const containsSearchTerm = !searchTerm || matchesSearchTerm(dataItem, columnsToSearch, searchTerm.toLowerCase());
                return containsFilter && containsSearchTerm;
            });
        }
        return filtered;
    }
}
